import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import styled from 'styled-components';

import SwipeIndicator from 'stile-shared/src/components/2024/common/SwipeIndicator/SwipeIndicator';

export interface Lesson {
  coverImage: string;
  name: string;
  description: string;
  url?: string;
}

interface LessonSwiperProps {
  lessons: Lesson[];
  heading: string;
}

const SpotlightTitle = styled.h5`
  grid-column: 1 / -3 !important;
  color: var(--stile-grey-8);
  margin: 0 0 20px;
  padding: 0;
  white-space: nowrap;
  text-align: left;
  text-wrap: wrap;
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const LessonSwiperContainer = styled.div`
  .content {
    flex-direction: row;
  }
  display: grid;
  grid-column: span 12;
  position: relative;
  width: 100%;
  height: auto;
  padding: 0;
  margin: 0 auto;
  background-color: var(--stile-grey-1);
  @media (max-width: 768px) {
    max-width: 100vw;
    padding: 0 0 32px;
    gap: 0;
    &:first-child {
      margin-top: 24px;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const LessonMenuContainer = styled.div`
  z-index: 30;
  display: flex;
  gap: 28px;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding: 0 calc(((100% - 1160px) / 2) - 28px);
  @media (max-width: 768px) {
    padding: 0 20px;
  }

  .lesson-tile {
    overflow: hidden;
    width: calc(100% / 4);
    max-width: 368px;
    min-width: 300px;
    @media (max-width: 768px) {
      width: 70%;
    }
  }

  & > div {
    scroll-snap-align: start;
  }

  &::before,
  &::after {
    content: '';
    display: block;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    margin: 0;
    padding: 0 10px;
  }

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

// Lesson Tile Component
export const LessonImage = styled.img.attrs(() => ({
  onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).src =
      'https://stileeducation.com/au/static/8e43f4232292ec71fa2fe7e771eec120/83395/in_class.webp';
  },
}))`
  width: 100%;
  height: auto;
  // aspect-ratio: 16 / 9;
  border-radius: 1rem;
  border: 1px solid var(--stile-grey-2);
  box-shadow: 0px 5px 11px 0px #00000012;
  z-index: 20;
  object-fit: cover;
  opacity: 1;

  @media (max-width: 768px) {
    grid-column: span 8;
    border-radius: 0.92em;
    border-width: 0.92px;
    box-shadow: 0px 5px 11px 0px #00000012;
  }
`;

export const LessonGroup = styled.div`
  grid-column: span 4;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  gap: 3px;
  margin: 0 0 60px;

  @media (max-width: 768px) {
    grid-column: span 12;
    width: 100%;
    flex-shrink: 0;
    margin: 0;
  }
`;

export const LessonName = styled.p`
  margin: 12px 0 0 0;
`;

export const Description = styled.h6`
  color: black;
  width: 100%;
  margin: 0;
  padding: 0;
  text-wrap: wrap;
`;

export interface LessonTileProps {
  lesson: Lesson;
}

export const LessonTile: React.FC<LessonTileProps> = ({ lesson }) => (
  <LessonGroup className="lesson-tile">
    <LessonImage src={lesson.coverImage} alt={lesson.name} />
    <LessonName className="--small">{lesson.name}</LessonName>
    <Description className="h7">{lesson.description}</Description>
  </LessonGroup>
);

const LessonSwiperComponent = forwardRef<HTMLDivElement, LessonSwiperProps>(
  ({ lessons, heading }, ref) => {
    const localRef = useRef<HTMLDivElement>(null!);

    useImperativeHandle(ref, () => localRef.current);

    return (
      <LessonSwiperContainer>
        <div className="content">
          <SpotlightTitle>{heading}</SpotlightTitle>
          <SwipeIndicator containerRef={localRef} totalItems={lessons.length} />
        </div>
        <LessonMenuContainer ref={localRef}>
          {lessons.map((lesson, index) => (
            <LessonTile key={`${lesson.name}-${index}`} lesson={lesson} />
          ))}
        </LessonMenuContainer>
      </LessonSwiperContainer>
    );
  }
);

LessonSwiperComponent.displayName = 'LessonSwiperComponent';

export default LessonSwiperComponent;
